import type {TFunction} from "i18next";
import type {ApiError} from "../api/client";

export type ErrorContext = "signup" | "signin" | "email-confirm" | "phone-request" | "phone-confirm" | "password-reset-request" | "password-reset-complete" | "session";

export interface LocalizedAPIError {
  message: string;
  requestId: string;
  fields: Record<string, string>;
}

export function localizeAPIError(error: ApiError, context: ErrorContext, t: TFunction): LocalizedAPIError {
  const fields: Record<string, string> = {};
  for (const field of Object.keys(error.fields)) {
    if (field.startsWith("captcha_")) continue;
    fields[field] = error.code === "client_required"
      ? t("errors.required")
      : error.code === "client_contact_required"
        ? t("errors.contactRequired")
      : error.code === "client_password_mismatch"
        ? t("errors.passwordMismatch")
        : error.code === "client_otp_format"
          ? t("errors.otpFormat")
        : t("errors.fieldInvalid");
  }

  const passwordResetProofFailure = context === "password-reset-complete"
    && (isPasswordResetProofCode(error.code) || (error.code === "validation" && Object.keys(error.fields).length === 0));

  return {
    message: passwordResetProofFailure ? t("errors.passwordResetInvalid") : messageForCode(error.code, context, t),
    requestId: error.requestId,
    fields,
  };
}

export function sessionErrorMessage(code: string, t: TFunction): string {
  return messageForCode(code, "session", t);
}

function messageForCode(code: string, context: ErrorContext, t: TFunction): string {
  if (code === "client_required") return t("errors.required");
  if (code === "client_contact_required") return t("errors.contactRequired");
  if (code === "client_password_mismatch") return t("errors.passwordMismatch");
  if (code === "client_otp_format") return t("errors.otpFormat");
  if (code === "missing_confirmation_token") return t("errors.missingConfirmationToken");
  if (code === "password_reused") return t("errors.passwordReused");
  if (code === "captcha_required") return t("errors.captchaRequired");
  if (code === "captcha_unavailable") return t("errors.captchaUnavailable");
  if (code === "anti_brute_force_unavailable") return t("errors.antiBruteForceUnavailable");
  if (context === "signin" && code === "unauthenticated") return t("errors.invalidCredentials");
  if (context === "email-confirm" && (code === "not_found" || code === "validation")) return t("errors.confirmationInvalid");
  if (context === "email-confirm" && code === "not_allowed") return t("errors.confirmationNotAllowed");
  if (context === "signup" && code === "already_exists") return t("errors.alreadyExists");
  if (context === "phone-confirm" && code === "validation") return t("errors.otpInvalid");
  if (context === "phone-confirm" && code === "not_allowed") return t("errors.otpExhausted");
  if (context === "phone-confirm" && code === "not_found") return t("errors.otpExpired");
  if ((context === "phone-request" || context === "phone-confirm") && code === "already_exists") return t("errors.phoneTaken");
  if ((context === "phone-request" || context === "phone-confirm") && code === "conflict") return t("errors.otpResendEarly");

  const keyByCode = {
    validation: "errors.validation",
    unauthenticated: "errors.unauthenticated",
    not_allowed: "errors.notAllowed",
    not_found: "errors.notFound",
    already_exists: "errors.alreadyExists",
    conflict: "errors.conflict",
    rate_limited: "errors.rateLimited",
    unavailable: "errors.unavailable",
    network_error: "errors.unavailable",
    internal: "errors.internal",
    client_error: "errors.unknown",
  } as const;

  return t(keyByCode[code as keyof typeof keyByCode] ?? "errors.unknown");
}

function isPasswordResetProofCode(code: string): boolean {
  return ["password_reset_invalid", "invalid_password_reset", "not_found", "not_allowed", "unauthenticated", "conflict"].includes(code);
}
